import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { adminService } from '../../services/api';
import { User, CreateUserData, PaginatedResponse } from '../../types';
import DataTable from '../common/DataTable';
import LoadingSpinner from '../common/LoadingSpinner';
import UserForm from './UserForm';
import { userRoles, ROLE_LABELS } from '../../utils/roles';

interface UserManagementProps {
  isVisible: boolean;
}

const UserManagement: React.FC<UserManagementProps> = ({ isVisible }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const queryClient = useQueryClient();

  // Récupération des utilisateurs
  const { data: usersData, isLoading } = useQuery<PaginatedResponse<User>>({
    queryKey: ['admin-users', searchTerm],
    keepPreviousData: true,
    queryFn: () => adminService.getUsers({ search: searchTerm }).then(res => res.data),
    enabled: isVisible,
  });

  // Mutations
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserData) => adminService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowForm(false);
      setEditingUser(null);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateUserData> }) =>
      adminService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowForm(false);
      setEditingUser(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: number) => adminService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id: number) => adminService.toggleUserStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const handleSubmit = (data: CreateUserData) => {
    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, data });
    } else {
      createUserMutation.mutate(data);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDelete = (user: User) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.first_name} ${user.last_name} ?`)) {
      deleteUserMutation.mutate(user.id);
    }
  };

  const handleToggleStatus = (user: User) => {
    toggleStatusMutation.mutate(user.id);
  };

  const columns = [
    {
      key: 'full_name',
      label: 'Nom complet',
      render: (_: any, user: User) => `${user.first_name} ${user.last_name}`,
    },
    {
      key: 'username',
      label: 'Nom d\'utilisateur',
    },
    {
      key: 'email',
      label: 'Email',
    },
    {
      key: 'role',
      label: 'Rôle(s)',
      render: (_: any, user: User) => {
        const colors: Record<string, string> = {
          admin: 'bg-purple-100 text-purple-800',
          dentist: 'bg-blue-100 text-blue-800',
          secretary: 'bg-green-100 text-green-800',
          comptable: 'bg-amber-100 text-amber-800',
        };
        const roles = userRoles(user);
        return (
          <div className="flex flex-wrap gap-1">
            {roles.map((r) => (
              <span key={r} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[r] || 'bg-gray-100 text-gray-800'}`}>
                {ROLE_LABELS[r] || r}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (_: any, user: User) => user.phone || '-',
    },
    {
      key: 'is_active',
      label: 'Statut',
      render: (_: any, user: User) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {user.is_active ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (_: any, user: User) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(user)}
            className="text-blue-600 hover:text-blue-900"
            title="Modifier"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleToggleStatus(user)}
            className={`${user.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
            title={user.is_active ? 'Désactiver' : 'Activer'}
          >
            {user.is_active ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => handleDelete(user)}
            className="text-red-600 hover:text-red-900"
            title="Supprimer"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (!isVisible) return null;

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Gestion des utilisateurs</h2>
          <p className="mt-1 text-sm text-gray-500">
            Gérez les comptes utilisateurs du système
          </p>
        </div>
        <button
          onClick={() => {
            setEditingUser(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nouvel utilisateur
        </button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm"
          />
        </div>
      </div>

      <DataTable
        data={usersData?.results || []}
        columns={columns}
        loading={isLoading}
      />

      {showForm && (
        <UserForm
          user={editingUser}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingUser(null);
          }}
          loading={createUserMutation.isPending || updateUserMutation.isPending}
        />
      )}
    </div>
  );
};

export default UserManagement;